import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  X as XIcon,
  Check,
  Trophy,
  ChevronRight,
  RotateCcw,
  Volume2,
  VolumeX,
  Maximize,
  Home,
  Zap,
} from "lucide-react";

const boardStyles = `
.lb-tile { perspective: 1000px; }
.lb-tile-inner { position: relative; width: 100%; height: 100%; transform-style: preserve-3d; transition: transform 0.55s cubic-bezier(0.4,0,0.2,1); }
.lb-tile-inner.flipped { transform: rotateX(180deg); }
.lb-face { position: absolute; inset: 0; display: flex; align-items: center; -webkit-backface-visibility: hidden; backface-visibility: hidden; border-radius: 0.9rem; }
.lb-back { transform: rotateX(180deg); }
@keyframes lb-pop { 0% { transform: scale(0.6); opacity: 0; } 60% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }
.lb-pop { animation: lb-pop 0.35s ease-out; }
`;

function useSounds(enabled) {
  const ctxRef = useRef(null);
  const ctx = () => {
    if (!enabled) return null;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) ctxRef.current = new AC();
    }
    return ctxRef.current;
  };
  const tone = (freq, dur = 0.16, type = "sine", when = 0) => {
    const c = ctx();
    if (!c) return;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.value = freq;
    o.connect(g);
    g.connect(c.destination);
    const t0 = c.currentTime + when;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  };
  return {
    correct: () => tone(660, 0.18, "triangle"),
    wrong: () => tone(160, 0.32, "sawtooth"),
    win: () => { [523, 659, 784, 1046].forEach((f, i) => tone(f, 0.22, "triangle", i * 0.14)); },
  };
}

export default function LiveBoard({
  room,
  role = "host",
  onPick,
  onNext,
  onResetRound,
  onSetTurn,
  onEnd,
  onExit,
  onNewMatch,
  soundOn = true,
  onToggleSound,
}) {
  const { t } = useTranslation();
  const sounds = useSounds(soundOn);
  const lastVerRef = useRef(null);

  const isHost = role === "host";
  const myTeam = role === "A" ? "A" : role === "B" ? "B" : null;

  useEffect(() => {
    if (!room) return;
    if (lastVerRef.current === null) { lastVerRef.current = room.version; return; }
    if (room.version !== lastVerRef.current) {
      lastVerRef.current = room.version;
      if (room.status === "finished") sounds.win();
      else if (room.last_result?.kind === "correct") sounds.correct();
      else if (room.last_result?.kind === "decoy") sounds.wrong();
    }
  }, [room, sounds]);

  if (!room) {
    return <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center">{t("common.loading")}</div>;
  }

  const teamAName = room.team_a_name || t("games.team_a");
  const teamBName = room.team_b_name || t("games.team_b");
  const activeTeam = room.active_team;
  const winner = room.winner;

  const goFullscreen = () => {
    const el = document.documentElement;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else el.requestFullscreen?.();
  };

  // ---------- WINNER ----------
  if (room.status === "finished") {
    return (
      <div className="min-h-screen bg-[#0A1329] text-white grid place-items-center px-6" data-testid="live-winner">
        <style>{boardStyles}</style>
        <div className="w-full max-w-lg text-center bg-gradient-to-b from-[#0F1E3D] to-[#0A1329] border-2 border-[#F59E0B] p-8 rounded-3xl shadow-[0_0_50px_rgba(245,158,11,0.3)] lb-pop">
          <Trophy size={56} className="mx-auto text-[#FDE68A]" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FDE68A] mt-4">{t("games.winner")}</div>
          <h1 className="font-display text-4xl font-extrabold mt-2" data-testid="live-winner-name">{winner || t("games.tie")}</h1>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-[#0A1329] border border-[#1E3A8A] rounded-2xl p-4">
              <div className="text-xs text-[#3B82F6] font-semibold truncate">{teamAName}</div>
              <div className="font-mono text-4xl font-black mt-1">{room.score_a}</div>
            </div>
            <div className="bg-[#0A1329] border border-[#6B3E2E] rounded-2xl p-4">
              <div className="text-xs text-[#F59E0B] font-semibold truncate">{teamBName}</div>
              <div className="font-mono text-4xl font-black mt-1">{room.score_b}</div>
            </div>
          </div>
          <p className="text-slate-400 text-sm mt-6">{t("games.live.match_saved")}</p>
          <div className="mt-4 flex flex-col gap-2">
            {isHost && onNewMatch && (
              <button onClick={onNewMatch} data-testid="live-new-match"
                className="bg-[#1E3A8A] text-white font-bold py-2.5 rounded-xl hover:brightness-125 transition-[filter]">
                {t("games.live.new_match")}
              </button>
            )}
            {onExit && (
              <button onClick={onExit} className="text-slate-300 text-sm hover:text-white inline-flex items-center justify-center gap-1" data-testid="live-exit-winner">
                <Home size={14} /> {t("games.back_to_games")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const canPick =
    !isHost && myTeam && room.status === "playing" && !room.round_over && activeTeam === myTeam;

  const turnLabel = () => {
    if (room.round_over) return t("games.live.round_over");
    const name = activeTeam === "A" ? teamAName : teamBName;
    if (room.stealing) return t("games.live.steal_banner", { team: name });
    if (myTeam) return activeTeam === myTeam ? t("games.live.your_turn") : t("games.live.other_turn", { team: name });
    return t("games.live.turn_of", { team: name });
  };

  return (
    <div className="min-h-screen bg-[#0A1329] text-white pb-32" data-testid="live-board">
      <style>{boardStyles}</style>

      <div className="max-w-6xl mx-auto px-4 pt-5">
        {/* Scoreboard */}
        <div className="flex items-center justify-between gap-3">
          <TeamScore name={teamAName} score={room.score_a} tone="blue" active={activeTeam === "A"} testid="live-score-a" />
          <div className="text-center shrink-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">{t("games.question")}</div>
            <div className="font-mono text-2xl font-bold" data-testid="live-question-counter">
              {(room.current_qi ?? 0) + 1}/{room.total_questions}
            </div>
            {room.turn_mode === "strikes" && (
              <div className="flex items-center justify-center gap-1.5 mt-1" data-testid="live-strikes">
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`w-6 h-6 rounded grid place-items-center ${i < (room.strikes || 0) ? "bg-[#EF4444]" : "bg-white/10"}`}>
                    {i < (room.strikes || 0) ? <XIcon size={14} strokeWidth={4} /> : null}
                  </span>
                ))}
              </div>
            )}
          </div>
          <TeamScore name={teamBName} score={room.score_b} tone="amber" active={activeTeam === "B"} testid="live-score-b" />
        </div>

        {/* Turn banner */}
        <div
          className={`mt-4 rounded-xl px-4 py-2.5 text-center font-bold text-sm ${
            room.stealing
              ? "bg-[#F59E0B]/20 border border-[#F59E0B] text-[#FDE68A]"
              : canPick
              ? "bg-[#10B981]/20 border border-[#10B981] text-[#A7F3D0]"
              : "bg-white/5 border border-white/10 text-slate-300"
          }`}
          data-testid="live-turn-banner"
        >
          {room.stealing && <Zap size={14} className="inline mr-1.5 -mt-0.5" />}
          {turnLabel()}
        </div>

        {/* Question */}
        <div className="mt-4 bg-[#0F1E3D] border border-[#1E3A8A] rounded-2xl px-6 py-5 text-center">
          <p className="font-display text-xl sm:text-2xl font-bold" data-testid="live-question-text">{room.question_text}</p>
          {room.turn_mode === "strikes" && (
            <div className="text-[#FDE68A] text-sm mt-1 font-mono" data-testid="live-round-points">
              {t("games.round_points")}: {room.round_points}
            </div>
          )}
        </div>

        {/* Board (revealed answers) */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="live-board-tiles">
          {(room.board || []).map((slot) => (
            <div key={slot.slot} className="lb-tile h-16 sm:h-[4.5rem]">
              <div className={`lb-tile-inner ${slot.revealed ? "flipped" : ""}`}>
                <div className="lb-face bg-gradient-to-br from-[#1E3A8A] to-[#0D1F47] border-2 border-[#3B82F6]/40 justify-center">
                  <span className="font-display text-2xl font-black text-white/70">{slot.slot + 1}</span>
                </div>
                <div className="lb-face lb-back bg-gradient-to-br from-[#10B981] via-[#059669] to-[#047857] border-2 border-[#A7F3D0] justify-between px-4">
                  <span className="font-display font-bold text-white text-base sm:text-lg leading-tight">{slot.text}</span>
                  <span className="font-mono font-black text-white text-2xl bg-white/25 rounded-lg px-2.5 py-0.5">{slot.points}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Options (10 choices) */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">{t("games.live.choose_answer")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="live-options">
            {(room.options || []).map((o) => {
              const picked = o.picked;
              const correct = o.result === "correct";
              const wrong = o.result === "wrong";
              const disabled = picked || !canPick;
              return (
                <button
                  key={o.key}
                  onClick={() => canPick && !picked && onPick?.(o.key)}
                  disabled={disabled}
                  data-testid={`live-option-${o.key}`}
                  className={`relative flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-left font-semibold transition-colors ${
                    correct
                      ? "bg-[#10B981]/20 border-[#10B981] text-[#A7F3D0]"
                      : wrong
                      ? "bg-[#EF4444]/20 border-[#EF4444] text-[#FCA5A5] line-through"
                      : canPick
                      ? "bg-[#0F1E3D] border-[#1E3A8A] hover:border-[#F59E0B] hover:bg-[#152244] cursor-pointer"
                      : "bg-[#0F1E3D]/60 border-white/10 text-slate-400 cursor-default"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {isHost && !picked && (
                      <span className={`w-2 h-2 rounded-full shrink-0 ${o.kind === "correct" ? "bg-[#10B981]" : "bg-[#EF4444]"}`} title={o.kind} />
                    )}
                    <span>{o.text}</span>
                  </span>
                  {correct && <Check size={18} className="shrink-0" />}
                  {wrong && <XIcon size={18} strokeWidth={3} className="shrink-0" />}
                </button>
              );
            })}
          </div>
          {isHost && (
            <p className="text-[11px] text-slate-500 mt-2">{t("games.live.host_key_hint")}</p>
          )}
        </div>
      </div>

      {/* Host control bar */}
      {isHost && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-[95vw] bg-slate-900/90 border border-slate-700/80 backdrop-blur-xl px-3 sm:px-5 py-2.5 rounded-2xl flex items-center gap-2 sm:gap-3 shadow-2xl">
          <button onClick={() => onSetTurn?.("A")} data-testid="live-set-turn-a" className={`text-white font-semibold text-sm px-3 py-2 rounded-xl transition-[filter] hover:brightness-125 max-w-[7rem] truncate ${activeTeam === "A" ? "bg-[#1E3A8A] ring-2 ring-[#3B82F6]" : "bg-[#1E3A8A]/60"}`}>
            {teamAName}
          </button>
          <button onClick={() => onSetTurn?.("B")} data-testid="live-set-turn-b" className={`text-white font-semibold text-sm px-3 py-2 rounded-xl transition-[filter] hover:brightness-125 max-w-[7rem] truncate ${activeTeam === "B" ? "bg-[#6B3E2E] ring-2 ring-[#F59E0B]" : "bg-[#6B3E2E]/60"}`}>
            {teamBName}
          </button>
          <div className="h-6 w-px bg-white/15" />
          <button onClick={onResetRound} title={t("games.reset_round")} data-testid="live-reset-round" className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
            <RotateCcw size={16} />
          </button>
          <button onClick={onNext} data-testid="live-next-question" className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 transition-colors font-semibold text-sm px-3 py-2 rounded-xl">
            <span className="hidden sm:inline">{t("games.next")}</span> <ChevronRight size={16} />
          </button>
          <button onClick={onEnd} data-testid="live-end-game" className="inline-flex items-center gap-1 bg-[#F59E0B] text-[#0A1329] font-bold text-sm px-3 py-2 rounded-xl hover:brightness-110 transition-[filter]">
            <Trophy size={15} /> <span className="hidden sm:inline">{t("games.finish")}</span>
          </button>
          <div className="h-6 w-px bg-white/15 hidden sm:block" />
          {onToggleSound && (
            <button onClick={onToggleSound} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors hidden sm:inline-flex" data-testid="live-toggle-sound">
              {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
          )}
          <button onClick={goFullscreen} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors hidden sm:inline-flex" data-testid="live-toggle-fullscreen">
            <Maximize size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

function TeamScore({ name, score, tone, active, testid }) {
  const ring = tone === "blue" ? "border-[#1E3A8A] text-[#3B82F6]" : "border-[#6B3E2E] text-[#F59E0B]";
  const glow = active ? (tone === "blue" ? "ring-2 ring-[#3B82F6] shadow-[0_0_28px_rgba(59,130,246,0.35)]" : "ring-2 ring-[#F59E0B] shadow-[0_0_28px_rgba(245,158,11,0.35)]") : "";
  return (
    <div className={`flex-1 max-w-[38%] bg-[#0F1E3D] border-2 rounded-2xl px-3 sm:px-5 py-3 transition-shadow ${ring} ${glow}`} data-testid={testid}>
      <div className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide truncate">{name}</div>
      <div className="font-mono text-4xl sm:text-5xl font-black text-white leading-none mt-1">{score}</div>
    </div>
  );
}
